import { checkDescriptionQuality as requestDescriptionQuality } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/api/checkDescriptionQuality'
import {
  getEmptyDescriptionQualityResult,
  isDescriptionQualityBlocking,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/helpers'
import { IDescriptionQualityResult } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/models'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { updateFormDataField } from 'cng-common/features/form'
import Logger from 'core/utils/Logger'

interface IUseDescriptionQualityParams {
  formKey: string
  name?: string | null
  description?: string | null
  quality?: IDescriptionQualityResult | null
  validateOnOpening?: boolean
}

interface IDescriptionQualityRequest {
  requestKey: string
  promise: Promise<IDescriptionQualityResult | null>
}

interface ICheckDescriptionQualityOptions {
  force?: boolean
  descriptionValue?: string | null
}

export const useDescriptionQuality = ({
  formKey,
  name,
  description,
  quality,
  validateOnOpening = true,
}: IUseDescriptionQualityParams) => {
  const dispatch = useAppDispatch()
  // A new form keeps the default EMPTY in data, but only treats it as validation after blur/Apply.
  const [isDescriptionQualityValidationActive, setIsDescriptionQualityValidationActive] =
    useState(validateOnOpening)
  const shouldCheckInitialQuality = validateOnOpening && !quality && Boolean(description?.trim())
  const [isChecking, setIsChecking] = useState(shouldCheckInitialQuality)
  const descriptionKey = useMemo(() => description ?? '', [description])
  const currentRequestKey = useMemo(
    () => JSON.stringify([name ?? '', description ?? '']),
    [description, name]
  )
  const currentRequestKeyRef = useRef(currentRequestKey)
  const previousDescriptionKeyRef = useRef(descriptionKey)
  const qualityRef = useRef(quality)
  const lastResultRef = useRef<{
    descriptionKey: string
    result: IDescriptionQualityResult
  } | null>(quality ? { descriptionKey, result: quality } : null)
  const activeRequestRef = useRef<IDescriptionQualityRequest | null>(null)
  const shouldCheckInitialQualityRef = useRef(shouldCheckInitialQuality)

  currentRequestKeyRef.current = currentRequestKey
  qualityRef.current = quality

  const updateQuality = useCallback(
    (value: IDescriptionQualityResult | null) => {
      dispatch(
        updateFormDataField({
          formKey,
          field: 'descriptionQuality',
          value,
          ignoreModified: true,
        })
      )
    },
    [dispatch, formKey]
  )

  useEffect(() => {
    if (previousDescriptionKeyRef.current === descriptionKey) {
      return
    }

    previousDescriptionKeyRef.current = descriptionKey
    lastResultRef.current = null
    activeRequestRef.current = null
    setIsChecking(false)

    if (qualityRef.current) {
      updateQuality(null)
    }
  }, [descriptionKey, updateQuality])

  const checkDescriptionQuality = useCallback(
    async ({ force = false, descriptionValue }: ICheckDescriptionQualityOptions = {}) => {
      setIsDescriptionQualityValidationActive(true)
      const requestDescription = descriptionValue === undefined ? description : descriptionValue
      const requestDescriptionKey = requestDescription ?? ''
      const requestKey = JSON.stringify([name ?? '', requestDescriptionKey])
      const emptyDescriptionQuality = getEmptyDescriptionQualityResult(requestDescription)

      if (emptyDescriptionQuality) {
        activeRequestRef.current = null
        lastResultRef.current = {
          descriptionKey: requestDescriptionKey,
          result: emptyDescriptionQuality,
        }
        setIsChecking(false)
        updateQuality(emptyDescriptionQuality)

        return emptyDescriptionQuality
      }

      const activeRequest = activeRequestRef.current

      if (activeRequest?.requestKey === requestKey) {
        return activeRequest.promise
      }

      if (!force && lastResultRef.current?.descriptionKey === requestDescriptionKey) {
        return lastResultRef.current.result
      }

      setIsChecking(true)

      const promise = requestDescriptionQuality({
        Name: name ?? '',
        Description: requestDescription ?? '',
      })
        .then((result) => {
          if (currentRequestKeyRef.current !== requestKey) {
            return null
          }

          lastResultRef.current = { descriptionKey: requestDescriptionKey, result }
          updateQuality(result)

          return result
        })
        .catch((error) => {
          Logger.error('Error checking Key Initiative description quality', error)

          const currentQuality = qualityRef.current

          if (currentRequestKeyRef.current === requestKey) {
            lastResultRef.current = currentQuality
              ? { descriptionKey: requestDescriptionKey, result: currentQuality }
              : null
          }

          return currentQuality ?? null
        })
        .finally(() => {
          if (activeRequestRef.current?.promise === promise) {
            activeRequestRef.current = null
            setIsChecking(false)
          }
        })

      activeRequestRef.current = { requestKey, promise }

      return promise
    },
    [description, name, updateQuality]
  )

  useEffect(() => {
    if (!shouldCheckInitialQualityRef.current) {
      return
    }

    shouldCheckInitialQualityRef.current = false
    void checkDescriptionQuality()
  }, [checkDescriptionQuality])

  const handleDescriptionBlur = useCallback(
    (descriptionValue?: string | null) => {
      void checkDescriptionQuality({ descriptionValue })
    },
    [checkDescriptionQuality]
  )

  return {
    checkDescriptionQuality,
    handleDescriptionBlur,
    isDescriptionQualityValidationActive,
    isDescriptionQualityBlocking:
      isDescriptionQualityValidationActive && isDescriptionQualityBlocking(quality),
    isDescriptionQualityChecking: isChecking,
  }
}
